import { BundleCartItemWithProduct } from '@storefront/adapters/LineItemAdapter';
import {
  BundleItem,
  BundleItemOption,
  BundleProduct,
  ProductInterface,
} from '@storefront/data-commerce';
import {
  getComplexOptions,
  getCoreOffer,
  getHiddenSimpleOptions,
  getProductTypeLabelFromItem,
  getSKUByUID,
  getUIDFromRequiredSKUItems,
} from '@storefront/utils/prodRequests';

import {
  BundleItemOptionWithLowestPrice,
  BundleProductWithLowestPrice,
} from '../app/pdp-config/printer/product-configuration/product-configuration.utils';
import { BundleProductItemType } from '@storefront/types/Product';

export enum PriceType {
  TRANSACTIONAL,
  SUBSCRIPTION,
}

// round float and set 2 precision digits
function priceFormatter(n: number | string): string {
  const valueFormat = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const formatted =
    typeof n === 'number' ? valueFormat.format(n) : valueFormat.format(+n);
  return formatted.toString();
}

function formattedPriceToNumber(n: number | string): string {
  return priceFormatter(n);
}

const isLeaseReceivable = (bundleProduct: BundleProduct): boolean =>
  !!bundleProduct.items.find(
    (item) => getProductTypeLabelFromItem(item) === 'leaseReceivable'
  )?.options?.[0]?.product;

const getPaymentPeriod = (bundleProduct: BundleProduct): number => {
  const lease = bundleProduct.items.find(
    (item) => getProductTypeLabelFromItem(item) === 'leaseReceivable'
  )?.options?.[0]?.product;

  return lease?.hp_contract_duration || 1;
};

const getRegularPriceFromBundle = (
  bundleItemOption: BundleItemOption | BundleItemOptionWithLowestPrice
): number => {
  const regularPrice =
    bundleItemOption?.product?.price_range?.minimum_price?.regular_price?.value;
  const finalprice =
    bundleItemOption?.product?.price_range?.minimum_price?.final_price?.value;
  return regularPrice ?? finalprice;
};

const getRegularPriceFromProduct = (
  bundleProduct: BundleProductWithLowestPrice
): number =>
  bundleProduct?.items
    ?.find((item) => item.title === BundleProductItemType.INSTANT_INK)
    ?.options?.find((option) => option.hp_plan_option === 'available')
    ?.lowest_price;

const getHpAsLowAsPrice = (bundleItemOption: BundleItemOption): number =>
  bundleItemOption?.product?.hp_as_low_as_price;

const getHpPriceType = (bundleItemOption: BundleItemOption): number =>
  bundleItemOption?.product?.hp_price_type;

const getFinalPriceFromProductData = (product: ProductInterface) =>
  product?.price_range?.minimum_price?.final_price?.value;

const getPricesFromCart = (bundleCartItem: BundleCartItemWithProduct) =>
  bundleCartItem?.prices?.price?.value;

const getMonthlyPriceFromProductInterface = (
  bundleItemOption: BundleItemOption
) => {
  const regularPrice = getRegularPriceFromBundle(bundleItemOption);
  const hpAsLowAsPrice = getHpAsLowAsPrice(bundleItemOption);
  const hp_price_type = getHpPriceType(bundleItemOption);
  if (hp_price_type === PriceType.SUBSCRIPTION) {
    return regularPrice;
  }
  return hpAsLowAsPrice;
};

const getMonthlySubscriptionPriceFromProductInterface = (
  bundleItemOption: BundleItemOption
) => {
  const regularPrice = getRegularPriceFromBundle(bundleItemOption);
  const hp_price_type = getHpPriceType(bundleItemOption);
  if (hp_price_type === PriceType.SUBSCRIPTION) {
    return regularPrice;
  }
  return 0;
};

const getMonthlyDevicePriceFromProductInterface = (
  bundleItemOption: BundleItemOption
) => {
  const hpAsLowAsPrice = getHpAsLowAsPrice(bundleItemOption);
  const hp_price_type = getHpPriceType(bundleItemOption);
  if (hp_price_type === PriceType.SUBSCRIPTION) {
    return 0;
  }
  return hpAsLowAsPrice;
};

const getTotalDevicePriceFromProductInterface = (
  bundleItemOption: BundleItemOption
) => {
  const hpRegularPrice = getRegularPriceFromBundle(bundleItemOption);
  const hp_price_type = getHpPriceType(bundleItemOption);
  if (hp_price_type === PriceType.SUBSCRIPTION) {
    return 0;
  }
  return hpRegularPrice;
};

const getTotalPriceFromProductInterface = (
  bundleItemOption: BundleItemOption,
  period: number
): number => {
  const regularPrice = getRegularPriceFromBundle(bundleItemOption);
  const hp_price_type = getHpPriceType(bundleItemOption);
  if (hp_price_type === PriceType.SUBSCRIPTION) {
    return regularPrice * period;
  }
  return regularPrice;
};

const handleHiddenRequiredOptions = (
  item: BundleItem,
  product: BundleProduct
): BundleItemOption => {
  let option =
    item.options.find((option) => option.is_default) ?? item.options[0];

  if (option.product.hp_product_type_label === 'printer') {
    option = getCoreOffer(item, product);
  }
  return option;
};

/**
 * Calculate the prices for URL selected options
 * @param {BundleProduct} product
 * @param {Record<string, string>} selections
 * @returns {Object} price for a BundleProduct with selections
 * @returns {number} grandTotal When BundleProduct hp_price_type is 'subscription' returns regularPrice * getPaymentPeriod() else regularPrice
 * @returns {number} pricePerMonth When BundleProduct is 'subscription' regularPrice else hpAsLowAsPrice
 * @returns {number} subscriptionPricePerMonth When BundleProduct is 'subscription' regularPrice else 0
 * @returns {number} devicePricePerMonth When BundleProduct is 'subscription' 0 else hpAsLowAsPrice
 * @returns {number} totalDevicePrice When BundleProduct is 'subscription' 0 else regularPrice
 */
const getSelectedOptionsPrice = (
  product: BundleProductWithLowestPrice,
  selections: Record<string, string>
): {
  grandTotal: number;
  pricePerMonth: number;
  subscriptionPricePerMonth: number;
  devicePricePerMonth: number;
  totalDevicePrice: number;
  options: string[];
} => {
  let grandTotal = 0;
  let pricePerMonth = 0;
  let subscriptionPricePerMonth = 0;
  let devicePricePerMonth = 0;
  let totalDevicePrice = 0;
  let options: string[] = [];
  const hiddenRequiredAndCoreOffer = [
    ...getComplexOptions(product),
    ...getHiddenSimpleOptions(product),
  ];

  const period = getPaymentPeriod(product);

  const addOptionAndUpdatePrice = (option: BundleItemOption) => {
    options = options.concat(option.uid);
    grandTotal += getTotalPriceFromProductInterface(option, period);
    pricePerMonth += getMonthlyPriceFromProductInterface(option);
    subscriptionPricePerMonth +=
      getMonthlySubscriptionPriceFromProductInterface(option);
    devicePricePerMonth += getMonthlyDevicePriceFromProductInterface(option);
    totalDevicePrice += getTotalDevicePriceFromProductInterface(option);
  };

  const hiddenRequiredBySku: Record<string, BundleItemOption> = {};
  /// prices for hidden but required options
  hiddenRequiredAndCoreOffer.forEach((item) => {
    // is only required for specific sku(s)
    if (
      item.options?.length > 1 &&
      item.options[0].product.hp_product_type_label !== 'leaseReceivable' &&
      item.options[0].product.hp_product_type_label !== 'printer'
    ) {
      item.options.forEach((option: any) => {
        const skus = option.required_sku.split(',');
        skus?.forEach((sku: any) => {
          hiddenRequiredBySku[sku] = option;
        });
      });
    } else {
      /// leaseReceivable will be handled here for simplicity, for core offer we need to check for preselected
      /// SKUs from url
      const option = handleHiddenRequiredOptions(item, product);
      addOptionAndUpdatePrice(option);
    }
  });
  const requiredSKU = getUIDFromRequiredSKUItems(product);
  for (const [fieldName, uid] of Object.entries(selections)) {
    // add prices of hidden required options specifics for selected skus
    const hiddenRequiredBySkuOption =
      hiddenRequiredBySku[getSKUByUID(product, uid)];
    if (hiddenRequiredBySkuOption) {
      addOptionAndUpdatePrice(hiddenRequiredBySkuOption);
    }
    // prices for hidden and not required with 'required_sku' options
    const optionalWithRequiredSKU = requiredSKU[uid];
    if (optionalWithRequiredSKU) {
      addOptionAndUpdatePrice(optionalWithRequiredSKU);
    }

    /// prices for selected options
    const item = product.items.find((item) => item.uid === fieldName);
    if (item) {
      const selected = item.options.find((item) => item.uid === uid);
      addOptionAndUpdatePrice(selected);
    }
  }
  return {
    grandTotal,
    pricePerMonth,
    subscriptionPricePerMonth,
    devicePricePerMonth,
    totalDevicePrice,
    options,
  };
};

export const getCurrencySymbol = (currencyCode: string): string => {
  const currencySymbols: { [key: string]: string } = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    CAD: 'C$',
    AUD: 'A$',
    BRL: 'R$',
    CNY: '¥',
    INR: '₹',
    MXN: '$',
  };

  return currencySymbols[currencyCode] || currencyCode;
};

export {
  formattedPriceToNumber,
  getFinalPriceFromProductData,
  getMonthlyPriceFromProductInterface,
  getPaymentPeriod,
  getPricesFromCart,
  getRegularPriceFromBundle as getRegularPrice,
  getRegularPriceFromProduct,
  getSelectedOptionsPrice,
  handleHiddenRequiredOptions,
  isLeaseReceivable,
  priceFormatter,
};
